import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { authService } from "../services/authService";
import { workshopService } from "../services/dataService";
import { Workshop } from "../types";
import AlertModal from "../components/AlertModal";
import "./Register.css";

interface RegisterForm {
  username: string;
  email: string;
  password: string;
  confirmPassword: string;
  firstName: string;
  lastName: string;
  role: string;
  workshopIds: string[]; // Changed from workshopId to workshopIds array
}

const Register: React.FC = () => {
  const navigate = useNavigate();
  const { user, isLoading } = useAuth();
  const [loading, setLoading] = useState(false);
  const [workshops, setWorkshops] = useState<Workshop[]>([]);
  const [formData, setFormData] = useState<RegisterForm>({
    username: "",
    email: "",
    password: "",
    confirmPassword: "",
    firstName: "",
    lastName: "",
    role: "Üretim", // Varsayılan rol
    workshopIds: [], // Initialize as empty array
  });

  const [errors, setErrors] = useState<Partial<RegisterForm>>({});

  // Alert Modal state
  const [alertModal, setAlertModal] = useState<{
    isOpen: boolean;
    message: string;
    type: "info" | "success" | "warning" | "error";
  }>({ isOpen: false, message: "", type: "info" });

  // Atölyeleri yükle
  useEffect(() => {
    const loadWorkshops = async () => {
      try {
        const data = await workshopService.getAll();
        // Atölyeleri displayOrder'a göre sırala
        const sortedData = [...data].sort((a, b) => {
          const orderA = a.displayOrder ?? 999;
          const orderB = b.displayOrder ?? 999;
          if (orderA !== orderB) return orderA - orderB;
          return a.name.localeCompare(b.name, "tr");
        });
        setWorkshops(sortedData);
      } catch (error) {
        console.error("Atölyeler yüklenemedi:", error);
      }
    };
    loadWorkshops();
  }, []);

  // Yetki kontrolü - Manager değilse anında yönlendir
  useEffect(() => {
    if (!isLoading && (!user || user.role !== "Manager")) {
      navigate("/orders");
    }
  }, [user, isLoading, navigate]);

  // Yetki kontrolü - Manager değilse hiçbir şey render etme
  if (isLoading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          fontSize: "18px",
        }}
      >
        Yükleniyor...
      </div>
    );
  }

  if (!user || user.role !== "Manager") {
    return null; // Hiçbir şey gösterme
  }

  // Form validation
  const validateForm = (): boolean => {
    const newErrors: Partial<RegisterForm> = {};

    if (!formData.email.trim()) {
      newErrors.email = "E-posta gereklidir";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Geçerli bir e-posta adresi giriniz";
    }

    if (!formData.password.trim()) {
      newErrors.password = "Şifre gereklidir";
    } else if (formData.password.length < 6) {
      newErrors.password = "Şifre en az 6 karakter olmalıdır";
    }

    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = "Şifreler eşleşmiyor";
    }

    if (!formData.firstName.trim()) {
      newErrors.firstName = "Ad gereklidir";
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = "Soyad gereklidir";
    }

    // Üretim ve Desinatör rolleri için atölye seçimi zorunlu
    if (
      (formData.role === "Üretim" || formData.role === "Modelist") &&
      formData.workshopIds.length === 0
    ) {
      newErrors.workshopIds =
        "Bu rol için en az bir atölye seçimi zorunludur" as any;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
      // Rol değiştiğinde ve seçilen rol Üretim veya Modelist değilse, workshopIds'yi temizle
      ...(name === "role" &&
        value !== "Üretim" &&
        value !== "Modelist" && { workshopIds: [] }),
    }));

    // Clear error when user starts typing
    if (errors[name as keyof RegisterForm]) {
      setErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);

      const registerData = {
        username: formData.username.trim(),
        email: formData.email.trim(),
        password: formData.password,
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        role: formData.role,
        workshopIds:
          formData.workshopIds.length > 0 ? formData.workshopIds : undefined,
        isActive: true,
        createdBy: user?.userId || "unknown",
      };

      await authService.register(registerData);

      setAlertModal({
        isOpen: true,
        message: `Kullanıcı "${formData.firstName} ${formData.lastName}" başarıyla oluşturuldu!`,
        type: "success",
      });

      // Formu temizle
      setFormData({
        username: "",
        email: "",
        password: "",
        confirmPassword: "",
        firstName: "",
        lastName: "",
        role: "Üretim",
        workshopIds: [],
      });
    } catch (error: any) {
      console.error("❌ Kullanıcı oluşturma hatası:", error);
      setAlertModal({
        isOpen: true,
        message:
          "Kullanıcı oluşturulamadı: " +
          (error.response?.data?.message || error.message),
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const userRoles = [
    { value: "Üretim", label: "Üretim" },
    { value: "Sekreterya", label: "Sekreterya" },
    { value: "Manager", label: "Manager" },
    { value: "Modelist", label: "Desinatör" },
    { value: "Muhasebe", label: "Muhasebe" },
  ];

  return (
    <div className="register-container">
      <div className="register-card">
        <div className="register-header">
          <h1>👤 Yeni Kullanıcı Ekle</h1>
          <p>Sisteme yeni kullanıcı eklemek için aşağıdaki formu doldurunuz</p>
        </div>

        <form onSubmit={handleSubmit} className="register-form">
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="firstName">Ad *</label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                className={errors.firstName ? "error" : ""}
                placeholder="Adınızı giriniz"
                required
              />
              {errors.firstName && (
                <span className="error-message">{errors.firstName}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="lastName">Soyad *</label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
                className={errors.lastName ? "error" : ""}
                placeholder="Soyadınızı giriniz"
                required
              />
              {errors.lastName && (
                <span className="error-message">{errors.lastName}</span>
              )}
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="email">E-posta *</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              className={errors.email ? "error" : ""}
              placeholder="E-posta adresini giriniz"
              required
            />
            {errors.email && (
              <span className="error-message">{errors.email}</span>
            )}
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="password">Şifre *</label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                className={errors.password ? "error" : ""}
                placeholder="Şifre (en az 6 karakter)"
                required
              />
              {errors.password && (
                <span className="error-message">{errors.password}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">Şifre Tekrar *</label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                className={errors.confirmPassword ? "error" : ""}
                placeholder="Şifreyi tekrar giriniz"
                required
              />
              {errors.confirmPassword && (
                <span className="error-message">{errors.confirmPassword}</span>
              )}
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="role">Kullanıcı Rolü *</label>
            <select
              id="role"
              name="role"
              value={formData.role}
              onChange={handleInputChange}
              className="role-select"
              required
            >
              {userRoles.map((role) => (
                <option key={role.value} value={role.value}>
                  {role.label}
                </option>
              ))}
            </select>
          </div>

          {/* Atölye seçimi Üretim ve Desinatör rolleri için */}
          {(formData.role === "Üretim" || formData.role === "Modelist") && (
            <div className="form-group">
              <label>Atölyeler *</label>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
                  gap: "6px",
                  padding: "8px",
                  border: "1px solid #e0e0e0",
                  borderRadius: "6px",
                  maxHeight: "150px",
                  overflowY: "auto",
                  backgroundColor: "#fafafa",
                }}
              >
                {workshops.map((workshop) => (
                  <label
                    key={workshop.workshopId}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      cursor: "pointer",
                      padding: "4px 6px",
                      fontSize: "13px",
                      borderRadius: "4px",
                      transition: "background-color 0.2s",
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.backgroundColor = "#f0f0f0")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.backgroundColor = "transparent")
                    }
                  >
                    <input
                      type="checkbox"
                      value={workshop.workshopId}
                      checked={formData.workshopIds.includes(
                        workshop.workshopId,
                      )}
                      onChange={(e) => {
                        const workshopId = e.target.value;
                        setFormData((prev) => ({
                          ...prev,
                          workshopIds: e.target.checked
                            ? [...prev.workshopIds, workshopId]
                            : prev.workshopIds.filter(
                                (id) => id !== workshopId,
                              ),
                        }));
                        // Clear error when user selects a workshop
                        if (errors.workshopIds) {
                          setErrors((prev) => ({
                            ...prev,
                            workshopIds: undefined,
                          }));
                        }
                      }}
                      style={{ width: "14px", height: "14px" }}
                    />
                    <span>{workshop.name}</span>
                  </label>
                ))}
              </div>
              {errors.workshopIds && (
                <span className="error-message">
                  {String(errors.workshopIds)}
                </span>
              )}
            </div>
          )}

          <div className="form-actions">
            <button
              type="button"
              onClick={() => navigate("/")}
              className="cancel-button"
              disabled={loading}
            >
              İptal
            </button>
            <button type="submit" className="submit-button" disabled={loading}>
              {loading ? "Oluşturuluyor..." : "Kullanıcı Oluştur"}
            </button>
          </div>
        </form>
      </div>

      {/* Alert Modal */}
      <AlertModal
        isOpen={alertModal.isOpen}
        message={alertModal.message}
        type={alertModal.type}
        onClose={() => setAlertModal({ ...alertModal, isOpen: false })}
      />
    </div>
  );
};

export default Register;
